import { useEffect, useState } from "react";
import {
  Autocomplete,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { Modal } from "../../../components/common/Modal/Modal";
import { ModalHeader } from "../../../components/common/Modal/ModalHeader";
import { ModalBody } from "../../../components/common/Modal/ModalBody";
import { ModalFooter } from "../../../components/common/Modal/ModalFooter";
import { Formik } from "formik";
import {
  Teacher,
  TeacherWebCreateEdit,
} from "../../../types/slices/teachersWebType";
import { useTeacherWebStore } from "../../../hooks/useTeacherWebStore";

const MAX_IMAGE_SIZE_KB = 1000;
const ALLOWED_TYPES = ["image/webp", "image/png", "image/jpeg", "image/jpg"];

export const CreateEditTeacherWebModal: React.FC<any> = ():
  | JSX.Element
  | any => {
  const {
    editTeacherWeb,
    openModal,
    setOpenModal,
    isEdit,
    createTeacherWebStore,
    editTeacherWebStore,
    teachers,
    selectedTeacher,
    getTeachersWeb,
    getTeacher,
    setSelectedTeacher,
  } = useTeacherWebStore();
  const [data, setData] = useState<TeacherWebCreateEdit>({
    teachers: {
      id: "",
      name: "",
      surname: "",
      dni: "",
    },
    teacher_id: 0,
    teacher: "",
    description: "",
    specialty: "",
    linkedin_url: "",
    image: "",
    order: 1,
    status: "1",
  });

  const validateImage = (file?: File) => {
    if (!file) return "La imagen es obligatoria";
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Formato inválido. Solo se permiten imágenes WEBP, PNG o JPG";
    }
    const sizeKB = file.size / 1024;
    if (sizeKB > MAX_IMAGE_SIZE_KB) {
      return `La imagen es muy pesada. Máximo ${MAX_IMAGE_SIZE_KB} KB`;
    }

    return undefined;
  };

  const validateForm = (values) => {
    let errors: any = {};
    if (!values.teacher && !values.teacher_id)
      errors.teacher_id = "Seleccione un docente";
    if (!values.description) errors.description = "Descripción es requerida";
    if (values.description.length > 80)
      errors.description = "El número máximo de caracteres es 80";
    if (!values.specialty) errors.specialty = "Especialidad es requerida";
    if (!values.linkedin_url) errors.linkedin_url = "URL es requerido";
    if (values.linkedin_url.length > 200)
      errors.linkedin_url = "El número máximo de caracteres es 200";
    if (!isEdit) {
      const imageError = validateImage(values.image);
      if (imageError) errors.image = imageError;
    }
    return errors;
  };

  const onSubmit = async (values) => {
    let params: any = {
      description: values.description,
      linkedin_url: values.linkedin_url,
      specialty: values.specialty,
      image: values.image,
      status: values.status,
    };
    if (selectedTeacher.id) {
      params = { ...params, teacher_id: selectedTeacher.id };
    } else {
      params = { ...params, teacher: values.teacher };
    }
    const method = isEdit
      ? editTeacherWebStore(editTeacherWeb?.id || 0, params)
      : createTeacherWebStore(params);
    const response = await method;
    if (response === true) {
      getTeachersWeb();
      setOpenModal(false);
    }
  };

  const clear = () => {
    setData({
      teachers: {
        id: "",
        name: "",
        surname: "",
        dni: "",
      },
      teacher_id: 0,
      description: "",
      specialty: "",
      linkedin_url: "",
      image: "",
      order: 1,
      status: "1",
    });
  };

  useEffect(() => {
    getTeacher();
    if (isEdit && openModal) {
      setData(editTeacherWeb);
      setSelectedTeacher(editTeacherWeb.teachers || ({} as Teacher));
    } else {
      setData({
        teachers: {
          id: "",
          name: "",
          surname: "",
          dni: "",
        },
        teacher_id: 0,
        description: "",
        specialty: "",
        linkedin_url: "",
        image: "",
        order: 1,
        status: "1",
      });
      setSelectedTeacher({} as Teacher);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editTeacherWeb, openModal]);

  const onChangeTeacher = (newValue: Teacher | null, setFieldValue: any) => {
    if (newValue) {
      setFieldValue("teacher_id", newValue.id);
      setSelectedTeacher(newValue);
    } else {
      setFieldValue("teacher_id", "");
      setSelectedTeacher({} as Teacher);
    }
  };

  return (
    <>
      <Modal
        open={openModal}
        handleClose={() => {
          clear();
          setOpenModal(false);
        }}
        disableEscapeKeyDown
        disableBackdropClick
        size="sm"
      >
        <ModalHeader
          text={isEdit ? "Editar Docente" : "Crear Docente"}
          className="positionElements"
          onCancel={() => {
            clear();
            setOpenModal(false);
          }}
        ></ModalHeader>

        <Formik
          initialValues={data}
          enableReinitialize
          validate={(values) => validateForm(values)}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleChange,
            setFieldValue,
            setFieldError,
          }) => {
            const preview = values.image
              ? typeof values.image === "string"
                ? values.image
                : URL.createObjectURL(values.image)
              : null;
            return (
              <>
                <ModalBody>
                  <form onSubmit={handleSubmit}>
                    <Grid container>
                      <Grid item container xs={12} spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                          <FormLabel>Profesor Existente:</FormLabel>
                          <Autocomplete
                            disablePortal
                            id="combo-box-teacher"
                            value={selectedTeacher}
                            options={teachers || []}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                error={Boolean(
                                  errors.teacher_id && touched.teacher_id,
                                )}
                                helperText={
                                  errors.teacher_id && touched.teacher_id
                                    ? errors.teacher_id
                                    : ""
                                }
                              />
                            )}
                            onChange={(e, newValue) => {
                              onChangeTeacher(newValue, setFieldValue);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <FormLabel>Profesor Nuevo:</FormLabel>
                          <TextField
                            id="teacher"
                            type="text"
                            name="teacher"
                            fullWidth
                            size="small"
                            value={values.teacher}
                            onChange={handleChange}
                            error={
                              errors.teacher && touched.teacher ? true : false
                            }
                            helperText={
                              errors.teacher && touched.teacher
                                ? errors.teacher
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Breve Descripción:</FormLabel>
                          <TextField
                            id="description"
                            type="text"
                            name="description"
                            fullWidth
                            size="small"
                            value={values.description}
                            onChange={handleChange}
                            error={
                              errors.description && touched.description
                                ? true
                                : false
                            }
                            helperText={
                              errors.description && touched.description
                                ? errors.description
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <FormLabel>Especialidad:</FormLabel>
                          <TextField
                            id="specialty"
                            type="text"
                            name="specialty"
                            fullWidth
                            size="small"
                            value={values.specialty}
                            onChange={handleChange}
                            error={
                              errors.specialty && touched.specialty
                                ? true
                                : false
                            }
                            helperText={
                              errors.specialty && touched.specialty
                                ? errors.specialty
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Enlace Linkedin:</FormLabel>
                          <TextField
                            id="linkedin_url"
                            type="text"
                            name="linkedin_url"
                            fullWidth
                            size="small"
                            value={values.linkedin_url}
                            onChange={handleChange}
                            error={
                              errors.linkedin_url && touched.linkedin_url
                                ? true
                                : false
                            }
                            helperText={
                              errors.linkedin_url && touched.linkedin_url
                                ? errors.linkedin_url
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Imagen (Máx. 1000 KB):</FormLabel>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={8}>
                              <TextField
                                id="image"
                                type="file"
                                name="image"
                                fullWidth
                                size="small"
                                inputProps={{
                                  accept:
                                    "image/webp,image/png,image/jpeg,image/jpg",
                                }}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  const file = event.target.files?.[0];
                                  if (!file) return;

                                  const error = validateImage(file);
                                  if (error) {
                                    setFieldError("image", error);
                                    setFieldValue("image", "");
                                    return;
                                  }

                                  setFieldValue("image", file);
                                  setFieldError("image", undefined);
                                }}
                                error={Boolean(errors.image && touched.image)}
                                helperText={
                                  errors.image && touched.image
                                    ? errors.image
                                    : ""
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              {preview ? (
                                <img
                                  src={preview}
                                  alt="Preview"
                                  loading="lazy"
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    objectFit: "cover",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    border: "1px dashed #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#999",
                                    fontSize: 12,
                                  }}
                                >
                                  Sin imagen
                                </div>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Estado:</FormLabel>
                          <TextField
                            select
                            id="status"
                            name="status"
                            fullWidth
                            size="small"
                            value={values.status}
                            onChange={handleChange}
                            error={
                              errors.status && touched.status ? true : false
                            }
                            helperText={
                              errors.status && touched.status
                                ? errors.status
                                : ""
                            }
                          >
                            <MenuItem value="1">Activo</MenuItem>
                            <MenuItem value="0">Inactivo</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </ModalBody>
                <ModalFooter
                  confirmText={isEdit ? "Actualizar" : "Registrar"}
                  onConfirm={handleSubmit}
                  cancelText={"Cancelar"}
                  onCancel={() => {
                    setOpenModal(false);
                  }}
                  className="modal-confirm-footer"
                />
              </>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};
